import { ActionTree } from 'vuex';
import { IGlobalTemplate } from '../types/global-template';
import { ITemplateGalleryState } from '../types/template-gallery-state';
import {
  defaultFilter,
  ETemplateGalleryAction,
  ETemplateGalleryMutation,
} from './template-gallery.constants';
import { RootState } from '@/store/store';
import { TemplateGalleryService } from '@/api/template-gallery/services/template-gallery.service';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { translate } from '@/helpers/translation.helper';
import { IResource } from '@/api/resources/types/resource';

export const actions: ActionTree<ITemplateGalleryState, RootState> = {
  async [ETemplateGalleryAction.LoadGlobalResources]({ commit, dispatch }) {
    commit(ETemplateGalleryMutation.SetLoaded, false);
    commit(ETemplateGalleryMutation.SetLoading, true);

    const { searchTerms, limit, offset, sortBy, sortOrder } = defaultFilter;

    try {
      const response: IResource[] = await TemplateGalleryService.loadGlobalResources(
        searchTerms,
        limit,
        offset,
        sortBy,
        sortOrder
      );
      dispatch(ETemplateGalleryAction.Load, response);
    } catch {
      dispatch(ETemplateGalleryAction.LoadFailed);
    }
  },
  async [ETemplateGalleryAction.Load]({ commit, dispatch }, globalResources: IResource[]) {
    commit(ETemplateGalleryMutation.SetLoaded, false);
    commit(ETemplateGalleryMutation.SetLoading, true);

    try {
      const mappedGlobalTemplates: IGlobalTemplate[] = await Promise.all(
        globalResources.map(
          async (resource: IResource): Promise<IGlobalTemplate> =>
            TemplateGalleryService.loadTemplate(resource.contentId)
        )
      );
      dispatch(ETemplateGalleryAction.LoadSuccess, mappedGlobalTemplates);
    } catch (e) {
      dispatch(ETemplateGalleryAction.LoadFailed);
    }
  },
  async [ETemplateGalleryAction.LoadSuccess]({ commit }, templates: IGlobalTemplate[]) {
    commit(ETemplateGalleryMutation.SetTemplates, templates);
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, true);
  },
  async [ETemplateGalleryAction.LoadFailed]({ commit, dispatch }) {
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-template-gallery-templates'),
      { root: true }
    );
  },
  async [ETemplateGalleryAction.SendOut]({ commit, dispatch }, templateId: string) {
    commit(ETemplateGalleryMutation.SetLoaded, false);
    commit(ETemplateGalleryMutation.SetLoading, true);
    try {
      const documentId: string = await TemplateGalleryService.sendOutTemplate(templateId);
      dispatch(ETemplateGalleryAction.SendOutSuccess, documentId);
    } catch (e) {
      dispatch(ETemplateGalleryAction.SendOutFailed);
    }
  },
  async [ETemplateGalleryAction.SendOutSuccess]({ commit }, documentId: string) {
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, true);
    commit(ETemplateGalleryMutation.SetDocumentId, documentId);
  },
  async [ETemplateGalleryAction.SendOutFailed]({ commit, dispatch }) {
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-send-out-global-template'),
      { root: true }
    );
  },
  async [ETemplateGalleryAction.Copy]({ commit, dispatch }, templateId: string) {
    commit(ETemplateGalleryMutation.SetLoaded, false);
    commit(ETemplateGalleryMutation.SetLoading, true);
    try {
      const newTemplateId: string = await TemplateGalleryService.copyTemplate(templateId);
      dispatch(ETemplateGalleryAction.CopySuccess, newTemplateId);
    } catch (e) {
      dispatch(ETemplateGalleryAction.CopyFailed);
    }
  },
  async [ETemplateGalleryAction.CopySuccess]({ commit }, newTemplateId: string) {
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, true);
    commit(ETemplateGalleryMutation.SetNewTemplateId, newTemplateId);
  },
  async [ETemplateGalleryAction.CopyFailed]({ commit, dispatch }) {
    commit(ETemplateGalleryMutation.SetLoading, false);
    commit(ETemplateGalleryMutation.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-copy-global-template'),
      { root: true }
    );
  },
};
